import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Upload, FileText, Building2, Cog } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const POLYMERS = ["PP (Polypropylene)", "PE (Polyethylene)", "PVC", "ABS", "PC (Polycarbonate)", "Nylon", "POM (Acetal)", "PMMA (Acrylic)", "Other / Custom Blend"];
const VOLUMES = ["< 1,000 pcs (prototype)", "1,000 – 10,000 pcs", "10,000 – 100,000 pcs", "100,000+ pcs (high volume)", "Recurring monthly order"];

const STEPS = [
  { id: 1, label: "Company", icon: Building2 },
  { id: 2, label: "Specs", icon: Cog },
  { id: 3, label: "Drawings", icon: FileText },
  { id: 4, label: "Review", icon: CheckCircle2 },
];

const Stepper = ({ step }) => (
  <div data-testid="rfq-stepper" className="flex items-center justify-between mb-10">
    {STEPS.map((s, i) => {
      const active = step === s.id;
      const done = step > s.id;
      return (
        <React.Fragment key={s.id}>
          <div className="flex items-center gap-3">
            <div
              data-testid={`stepper-step-${s.id}`}
              className={`w-9 h-9 grid place-items-center border ${active ? "bg-[#0dd3ce] text-[#03161D] border-[#0dd3ce]" : done ? "bg-[#0dd3ce]/20 text-[#5ce7e3] border-[#0dd3ce]/40" : "bg-[#0a3845] text-slate-500 border-[#0e4555]"}`}
            >
              <s.icon className="w-4 h-4" />
            </div>
            <div className="hidden sm:block">
              <div className={`text-[10px] uppercase tracking-[0.2em] ${active ? "text-[#0dd3ce]" : "text-slate-500"}`}>Step {s.id}</div>
              <div className={`text-sm font-semibold ${active ? "text-slate-100" : done ? "text-slate-300" : "text-slate-500"}`}>{s.label}</div>
            </div>
          </div>
          {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-3 ${done ? "bg-[#0dd3ce]/40" : "bg-slate-800"}`} />}
        </React.Fragment>
      );
    })}
  </div>
);

const Field = ({ label, required, children, hint }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.18em] text-slate-400 font-semibold">
      {label} {required && <span className="text-[#0dd3ce]">*</span>}
    </Label>
    {children}
    {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
  </div>
);

const inputCls = "bg-[#03161D] border border-[#0e4555] text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#0dd3ce] focus-visible:border-[#0dd3ce] rounded-none h-11";

export default function RFQPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    company_name: "", gstin: "", contact_person: "", contact_email: "", contact_phone: "",
    polymer_type: "", order_volume: "", additional_notes: "",
  });
  const [file, setFile] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 1) return form.company_name && form.contact_person && form.contact_email;
    if (step === 2) return form.polymer_type && form.order_volume;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      if (file) fd.append("file", file);
      await api.post("/rfqs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("RFQ submitted. Our team will respond shortly.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#062632]">
        <Header />
        <main className="max-w-3xl mx-auto px-6 md:px-12 py-24">
          <div data-testid="rfq-success" className="border border-[#0dd3ce]/40 bg-[#0a3845] p-10 fade-up">
            <CheckCircle2 className="w-12 h-12 text-[#0dd3ce] mb-6" />
            <h1 className="font-display text-3xl font-bold text-slate-100">Quote request received.</h1>
            <p className="mt-4 text-slate-400 leading-relaxed">
              Thank you. Our technical team at A3 Sagar Polymers has been notified and will review your
              specifications. Expect a response within <span className="text-[#0dd3ce] font-semibold">1–2 business days</span>.
            </p>
            <div className="mt-8 flex gap-3">
              <button data-testid="back-home-btn" onClick={() => navigate("/")} className="border border-[#0e4555] px-6 py-3 text-sm uppercase tracking-wider text-slate-200 hover:bg-slate-800">Back to Home</button>
              <button data-testid="submit-another-btn" onClick={() => { setDone(false); setStep(1); setForm({ company_name: "", gstin: "", contact_person: "", contact_email: "", contact_phone: "", polymer_type: "", order_volume: "", additional_notes: "" }); setFile(null); }} className="bg-[#0dd3ce] text-[#03161D] font-semibold px-6 py-3 text-sm uppercase tracking-wider hover:bg-[#5ce7e3]">Submit Another</button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#062632]">
      <Header />
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#0dd3ce] font-semibold mb-3">Request for Quote</div>
        <h1 data-testid="rfq-page-title" className="font-display text-4xl sm:text-5xl font-bold text-slate-100 tracking-tight">Procurement-grade quotation in 4 steps.</h1>
        <p className="mt-3 text-slate-400 max-w-2xl">Share your specs and drawings. Our engineering team will return with material guidance, tooling notes, and a clean B2B quote.</p>

        <div className="mt-12 border border-[#0a3a47] bg-[#0a3845] p-8 md:p-10">
          <Stepper step={step} />

          {step === 1 && (
            <div data-testid="step-1-panel" className="grid md:grid-cols-2 gap-6 fade-up">
              <Field label="Company Name" required>
                <Input data-testid="input-company" className={inputCls} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Acme Industries Pvt. Ltd." />
              </Field>
              <Field label="GSTIN" hint="Optional — speeds up B2B onboarding">
                <Input data-testid="input-gstin" className={inputCls} value={form.gstin} onChange={(e) => set("gstin", e.target.value.toUpperCase())} placeholder="27AAAAA0000A1Z5" />
              </Field>
              <Field label="Contact Person" required>
                <Input data-testid="input-contact-person" className={inputCls} value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} placeholder="Procurement Manager" />
              </Field>
              <Field label="Contact Email" required>
                <Input data-testid="input-contact-email" type="email" className={inputCls} value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="procurement@acme.com" />
              </Field>
              <Field label="Phone">
                <Input data-testid="input-contact-phone" className={inputCls} value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="+91" />
              </Field>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-2-panel" className="grid md:grid-cols-2 gap-6 fade-up">
              <Field label="Polymer Type Required" required>
                <Select value={form.polymer_type} onValueChange={(v) => set("polymer_type", v)}>
                  <SelectTrigger data-testid="select-polymer" className={inputCls}><SelectValue placeholder="Select polymer" /></SelectTrigger>
                  <SelectContent className="bg-[#0a3845] border-[#0e4555] text-slate-100">
                    {POLYMERS.map((p) => <SelectItem data-testid={`polymer-${p}`} key={p} value={p} className="focus:bg-[#0dd3ce]/15">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Expected Order Volume" required>
                <Select value={form.order_volume} onValueChange={(v) => set("order_volume", v)}>
                  <SelectTrigger data-testid="select-volume" className={inputCls}><SelectValue placeholder="Select volume tier" /></SelectTrigger>
                  <SelectContent className="bg-[#0a3845] border-[#0e4555] text-slate-100">
                    {VOLUMES.map((p) => <SelectItem data-testid={`volume-${p}`} key={p} value={p} className="focus:bg-[#0dd3ce]/15">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="md:col-span-2">
                <Field label="Additional Notes" hint="Tolerances, deadlines, post-processing, certifications…">
                  <Textarea data-testid="input-notes" className={`${inputCls} h-32 py-3`} value={form.additional_notes} onChange={(e) => set("additional_notes", e.target.value)} placeholder="Anything our engineering team should know…" />
                </Field>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-3-panel" className="fade-up">
              <Field label="Technical Drawing / Spec Sheet" hint="PDF, PNG, JPG, DWG, DXF — up to 15MB. Optional but recommended.">
                <label data-testid="file-drop-area" className="block border-2 border-dashed border-[#0e4555] hover:border-[#0dd3ce]/50 hover:bg-[#03161D] transition-colors p-10 cursor-pointer text-center">
                  <input data-testid="input-file" type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf,.step,.stp,.iges" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                  <Upload className="w-8 h-8 text-[#0dd3ce] mx-auto mb-3" />
                  {file ? (
                    <div>
                      <div className="text-slate-100 font-semibold">{file.name}</div>
                      <div className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB · click to replace</div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-slate-200 font-semibold">Drop file or click to upload</div>
                      <div className="text-xs text-slate-500 mt-1">PDF · DWG · DXF · STEP · PNG · JPG (max 15MB)</div>
                    </div>
                  )}
                </label>
              </Field>
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-4-panel" className="fade-up">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#0dd3ce] font-semibold mb-4">Review your submission</div>
              <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                {[
                  ["Company", form.company_name],
                  ["GSTIN", form.gstin || "—"],
                  ["Contact", `${form.contact_person} · ${form.contact_email}`],
                  ["Phone", form.contact_phone || "—"],
                  ["Polymer", form.polymer_type],
                  ["Volume", form.order_volume],
                  ["Notes", form.additional_notes || "—"],
                  ["Attachment", file ? file.name : "None"],
                ].map(([k, v]) => (
                  <div key={k} className="border-b border-[#0a3a47] pb-3">
                    <dt className="text-[10px] uppercase tracking-widest text-slate-500">{k}</dt>
                    <dd className="text-slate-100 mt-1 break-words">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <div className="mt-10 flex items-center justify-between border-t border-[#0a3a47] pt-6">
            <button
              data-testid="rfq-back-btn"
              disabled={step === 1}
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              className="inline-flex items-center gap-2 border border-[#0e4555] px-5 py-3 text-sm uppercase tracking-wider text-slate-300 hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            {step < 4 ? (
              <button
                data-testid="rfq-next-btn"
                disabled={!canNext()}
                onClick={() => setStep((s) => Math.min(4, s + 1))}
                className="inline-flex items-center gap-2 bg-[#0dd3ce] text-[#03161D] font-semibold px-6 py-3 text-sm uppercase tracking-wider hover:bg-[#5ce7e3] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                data-testid="rfq-submit-btn"
                onClick={submit}
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-[#0dd3ce] text-[#03161D] font-semibold px-6 py-3 text-sm uppercase tracking-wider hover:bg-[#5ce7e3] disabled:opacity-60"
              >
                {submitting ? "Submitting…" : "Submit RFQ"} <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
